/**
 * CheckoutContext - Shopping cart and checkout state management
 * Supports multi-class booking with persistence
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { SessionIdentifier } from '../api/events';
import { validateCoupon } from '../api/booking';
import { calculateDiscount } from '@sda/shared';
import { useAuth } from './AuthContext';

// =============================================================================
// Types
// =============================================================================

export interface CartItem {
  id: string; // Unique cart item ID
  tourStopId: string;
  classOptionId: string;
  className: string;
  showId: string;
  showName: string;
  showLogoUrl: string | null;
  venueName: string;
  venueCity: string;
  venueState: string;
  scheduledDate: string;
  sessionIdentifier?: SessionIdentifier;
  /** Widened to string — Monday slots use arbitrary 12h keys (e.g. "6:00 PM") */
  timeSlot: string;
  basePriceCents: number;
  travelSurchargeCents: number;
  totalPriceCents: number;
  durationMinutes: number;
  // Participant details
  studentCount: number | null;
  studentLevel: string | null;
  specialRequests: string | null;
  // Add-ons
  includeMarketingMaterials: boolean;
  groupTicketInquiry: boolean;
  // Session scheduling
  startTimePreference?: 'early' | 'default' | 'late';
  simultaneousSessions?: boolean;
  simultaneousTrigger?: string | null;
  // Monday multi-slot fields (all optional — stale localStorage carts parse fine)
  /** Exact start time snapshotted from server availability (HH:MM:SS). Present ⇒ exact slot, no preference applied. */
  slotStartTime?: string;
  /** Exact end time snapshotted from server availability (HH:MM:SS). Display-only; server recomputes from duration. */
  slotEndTime?: string;
  /** Day type — 'monday' items use exact slot time instead of session resolution. */
  dayType?: 'saturday' | 'sunday' | 'monday';
}

export interface AppliedCoupon {
  couponId: string;
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  appliesTo: 'total' | 'sessions_only' | 'addons_only';
}

export interface CartSummary {
  subtotalCents: number;
  travelSurchargeCents: number;
  marketingFeeCents: number;
  discountCents: number;
  couponCode: string | null;
  totalCents: number;
  depositCents: number;
  itemCount: number;
  isFullPayment: boolean;
}

export interface TermsAcceptance {
  accepted: boolean;
  version: string;
  timestamp: string;
  ipAddress?: string;
}

interface CheckoutContextType {
  // Cart state
  items: CartItem[];
  isLoading: boolean;

  // Cart actions
  addItem: (item: Omit<CartItem, 'id'>) => void;
  removeItem: (itemId: string) => void;
  updateItem: (itemId: string, updates: Partial<CartItem>) => void;
  clearCart: () => void;

  // Cart summary
  getCartSummary: () => CartSummary;

  // Coupon
  appliedCoupon: AppliedCoupon | null;
  couponError: string | null;
  isValidatingCoupon: boolean;
  applyCoupon: (code: string) => Promise<void>;
  removeCoupon: () => void;

  // Terms
  termsAcceptance: TermsAcceptance | null;
  acceptTerms: (version: string, ipAddress?: string) => void;

  // Contact selection for booking
  selectedContactId: string | null;
  setSelectedContactId: (contactId: string | null) => void;

  // ZIP code for pricing (from search context)
  userZip: string | null;
  setUserZip: (zip: string | null) => void;

  // Lat/lng for pricing (from Google Places autocomplete)
  userLat: number | null;
  userLng: number | null;

  // Convenience setter for location (zip + lat/lng together)
  setUserLocation: (location: { zip?: string | null; lat?: number | null; lng?: number | null }) => void;
}

// =============================================================================
// Constants
// =============================================================================

const STORAGE_KEY = 'sda_checkout_cart';
const MARKETING_FEE_CENTS = 5000; // $50
const DEPOSIT_PER_CLASS_CENTS = 10000; // $100

const couponErrorMessages: Record<string, string> = {
  COUPON_NOT_FOUND: 'Invalid coupon code. Please check and try again.',
  COUPON_EXPIRED: 'This coupon has expired.',
  COUPON_LIMIT_REACHED: 'This coupon has reached its usage limit.',
  COUPON_ALREADY_USED: 'This coupon has already been used by your studio.',
};

// =============================================================================
// Context
// =============================================================================

const CheckoutContext = createContext<CheckoutContextType | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface CheckoutProviderProps {
  children: ReactNode;
}

export function CheckoutProvider({ children }: CheckoutProviderProps) {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [termsAcceptance, setTermsAcceptance] = useState<TermsAcceptance | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [userZip, setUserZip] = useState<string | null>(null);
  const [userLat, setUserLat] = useState<number | null>(null);
  const [userLng, setUserLng] = useState<number | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setItems(parsed.items || []);
        setTermsAcceptance(parsed.termsAcceptance || null);
        setSelectedContactId(parsed.selectedContactId || null);
        setUserZip(parsed.userZip || null);
        setUserLat(parsed.userLat ?? null);
        setUserLng(parsed.userLng ?? null);
        setAppliedCoupon(parsed.appliedCoupon || null);
      }
    } catch (err) {
      console.error('Failed to load cart from storage:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Persist cart to localStorage
  useEffect(() => {
    if (isLoading) return;

    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          items,
          termsAcceptance,
          selectedContactId,
          userZip,
          userLat,
          userLng,
          appliedCoupon,
        })
      );
    } catch (err) {
      console.error('Failed to save cart to storage:', err);
    }
  }, [items, termsAcceptance, selectedContactId, userZip, userLat, userLng, appliedCoupon, isLoading]);

  // Generate unique ID for cart items
  const generateId = (): string => {
    return `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Add item to cart
  const addItem = useCallback((item: Omit<CartItem, 'id'>) => {
    const newItem: CartItem = {
      ...item,
      id: generateId(),
    };
    setItems((prev) => [...prev, newItem]);
  }, []);

  // Remove item from cart
  const removeItem = useCallback((itemId: string) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  // Update item in cart
  const updateItem = useCallback((itemId: string, updates: Partial<CartItem>) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item
      )
    );
  }, []);

  // Clear entire cart
  const clearCart = useCallback(() => {
    setItems([]);
    setTermsAcceptance(null);
    setSelectedContactId(null);
    setAppliedCoupon(null);
    setCouponError(null);
  }, []);

  // Calculate cart summary
  const getCartSummary = useCallback((): CartSummary => {
    const subtotalCents = items.reduce((sum, item) => sum + item.basePriceCents, 0);
    const travelSurchargeCents = items.reduce(
      (sum, item) => sum + item.travelSurchargeCents,
      0
    );
    // Marketing fee is per-session ($50 for each session with marketing enabled)
    const marketingFeeCents = items.filter((item) => item.includeMarketingMaterials).length * MARKETING_FEE_CENTS;
    const originalTotal = subtotalCents + travelSurchargeCents + marketingFeeCents;

    // Calculate discount if coupon applied
    let discountCents = 0;
    if (appliedCoupon) {
      discountCents = calculateDiscount({
        discountType: appliedCoupon.discountType,
        discountValue: appliedCoupon.discountValue,
        appliesTo: appliedCoupon.appliesTo,
        subtotalCents,
        travelSurchargeCents,
        marketingFeeCents,
      });
    }

    const totalCents = Math.max(0, originalTotal - discountCents);

    // 15-Day Rule: If any class is within 15 days, charge 100% upfront
    const now = new Date();
    const fifteenDaysFromNow = new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000);
    const isFullPayment = items.some(
      (item) => new Date(item.scheduledDate) <= fifteenDaysFromNow
    );

    // Deposit = $100 per class + marketing fee (travel surcharge billed with balance)
    const normalDeposit = isFullPayment
      ? totalCents
      : items.length * DEPOSIT_PER_CLASS_CENTS + marketingFeeCents;

    // If discounted total is less than normal deposit, cap deposit at total
    const depositCents = Math.min(normalDeposit, totalCents);

    return {
      subtotalCents,
      travelSurchargeCents,
      marketingFeeCents,
      discountCents,
      couponCode: appliedCoupon?.code ?? null,
      totalCents,
      depositCents,
      itemCount: items.length,
      isFullPayment,
    };
  }, [items, appliedCoupon]);

  // Apply coupon code
  const applyCoupon = useCallback(async (code: string): Promise<void> => {
    if (!user?.studio_id) {
      setCouponError('You must be logged in to apply a coupon.');
      return;
    }

    setIsValidatingCoupon(true);
    setCouponError(null);

    try {
      const result = await validateCoupon(code, user.studio_id);
      setAppliedCoupon({
        couponId: result.couponId,
        code: result.code,
        discountType: result.discountType,
        discountValue: result.discountValue,
        appliesTo: result.appliesTo,
      });
      setCouponError(null);
    } catch (err) {
      const errorCode = (err as Error & { code?: string }).code;
      const message = (errorCode && couponErrorMessages[errorCode])
        || (err instanceof Error ? err.message : 'Failed to validate coupon. Please try again.');
      setCouponError(message);
      setAppliedCoupon(null);
    } finally {
      setIsValidatingCoupon(false);
    }
  }, [user?.studio_id]);

  // Remove applied coupon
  const removeCoupon = useCallback(() => {
    setAppliedCoupon(null);
    setCouponError(null);
  }, []);

  // Set location (zip + lat/lng) as a convenience
  const setUserLocation = useCallback(
    (location: { zip?: string | null; lat?: number | null; lng?: number | null }) => {
      if (location.zip !== undefined) setUserZip(location.zip);
      if (location.lat !== undefined) setUserLat(location.lat);
      if (location.lng !== undefined) setUserLng(location.lng);
    },
    []
  );

  // Accept terms
  const acceptTerms = useCallback((version: string, ipAddress?: string) => {
    setTermsAcceptance({
      accepted: true,
      version,
      timestamp: new Date().toISOString(),
      ipAddress,
    });
  }, []);

  const value: CheckoutContextType = {
    items,
    isLoading,
    addItem,
    removeItem,
    updateItem,
    clearCart,
    getCartSummary,
    appliedCoupon,
    couponError,
    isValidatingCoupon,
    applyCoupon,
    removeCoupon,
    termsAcceptance,
    acceptTerms,
    selectedContactId,
    setSelectedContactId,
    userZip,
    setUserZip,
    userLat,
    userLng,
    setUserLocation,
  };

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useCheckout(): CheckoutContextType {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
}

// =============================================================================
// Utilities
// =============================================================================


/**
 * Format date for display
 */
export function formatBookingDate(dateString: string): string {
  const date = new Date(dateString + 'T12:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}
